import { Lock, FileText, CheckCircle, TrendingUp, Shield, ArrowRight, Zap, BarChart3, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface NotasFiscaisBlockedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotasFiscaisBlockedModal({
  open,
  onOpenChange
}: NotasFiscaisBlockedModalProps) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate("/assinatura");
  };

  const handleClose = () => {
    onOpenChange(false);
    navigate("/");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 [&>button]:hidden">
        <VisuallyHidden>
          <DialogTitle>Emita Notas Fiscais</DialogTitle>
        </VisuallyHidden>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-500 text-white p-6 flex items-center justify-between border-b">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Emita Notas Fiscais</h2>
              <p className="text-sm text-purple-100">Profissionalize seus documentos</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-white/20 rounded-full transition"
            title="Voltar para o Dashboard"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Benefícios */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                icon: FileText,
                title: "Emissão de NF-e",
                desc: "Notas Fiscais eletrônicas integradas"
              },
              {
                icon: CheckCircle,
                title: "Conformidade Legal",
                desc: "Tudo de acordo com a legislação"
              },
              {
                icon: TrendingUp,
                title: "Credibilidade",
                desc: "Seus clientes terão documentos oficiais"
              },
              {
                icon: BarChart3,
                title: "Relatórios",
                desc: "Controle fiscal detalhado"
              }
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200 hover:border-purple-400 transition-all"
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <Icon className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">
                        {benefit.title}
                      </h4>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {benefit.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Plano Atual vs Profissional */}
          <div className="bg-white rounded-lg p-5 border-2 border-gray-200 space-y-4">
            <h3 className="font-bold text-gray-900">Comparação de Planos</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold mb-2">
                  Seu Plano Atual
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-red-500">✗</span> Sem notas fiscais
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-red-500">✗</span> Sem documentação oficial
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-red-500">✗</span> Sem integração fiscal
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-3 border-2 border-purple-300">
                <p className="text-xs text-purple-600 uppercase font-bold mb-2">
                  🚀 Plano Profissional
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-purple-700 font-semibold">
                    <span className="text-green-600">✓</span> Até 100+ NF-e/mês
                  </div>
                  <div className="flex items-center gap-2 text-purple-700 font-semibold">
                    <span className="text-green-600">✓</span> Documentos oficiais
                  </div>
                  <div className="flex items-center gap-2 text-purple-700 font-semibold">
                    <span className="text-green-600">✓</span> Integração Focus NFe
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Destaque - Por que fazer upgrade */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-lg p-5 text-white space-y-3">
            <div className="flex gap-3">
              <Zap className="w-6 h-6 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-lg">Profissionalize seu Negócio</h4>
                <p className="text-sm text-purple-100 mt-1">
                  Notas fiscais emitidas diretamente do Ateliê Pro aumentam sua credibilidade com clientes, facilitam a contabilidade e garantem conformidade legal.
                </p>
              </div>
            </div>
          </div>

          {/* Casos de Uso */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-600" />
              Ideal Para:
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>✓ Ateliers que vendem para empresas</li>
              <li>✓ Prestadores de serviço (bordado, personalização)</li>
              <li>✓ Confecções e indústrias têxteis</li>
              <li>✓ Qualquer negócio que precisa de documentação fiscal</li>
            </ul>
          </div>
        </div>

        {/* Footer com Botões */}
        <div className="sticky bottom-0 bg-white border-t p-6 space-y-3">
          <Button
            onClick={handleUpgrade}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all text-base h-auto"
          >
            <Zap className="w-5 h-5 mr-2" />
            Fazer Upgrade Agora
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          <Button
            onClick={handleClose}
            variant="outline"
            className="w-full border-2 border-gray-300 text-gray-700 hover:bg-gray-50 py-3 rounded-lg font-medium"
          >
            Continuar Sem Notas Fiscais
          </Button>

          <p className="text-center text-xs text-gray-600 pt-2">
            ✅ Integração com Focus NFe • 📄 Válido legalmente • 🔒 Dados seguros
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
